import React, { useEffect, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { default as DefaultInput } from '@/components/Input';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next'; // Import the useTranslation hook
import { Partner, getPartner, savePartner } from '@/services/PartnerService';
import Spinner from '@/components/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import { getCompanyId } from '@/services/AuthService';
import { toast } from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import FormButtons from '@/components/shared/FormButtons';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('PartnerForm.nameRequired'),
});

interface FormValues extends Partner {
  name: string;
}

function PartnerForm() {
  const { id } = useParams();
  const editForm = id !== 'new';
  const navigate = useNavigate();

  const { t } = useTranslation(); // Initialize the useTranslation hook

  const [formikValuesState, setFormikValuesState] = useState<FormValues>({
    name: '',
    id: '',
  });
  const [loading, setLoading] = useState(false);

  const {
    data: partnerData,
    isLoading,
    refetch: refetchPartner,
  } = useQuery<Partner | undefined>(
    ['partner', id],
    async () => await getPartner(id!),
    {
      enabled: editForm,
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
      onSuccess: (partner) => {
        if (partner) {
          setFormikValuesState((prev) => ({ ...prev, ...partner }));
        }
      },
    }
  );

  useEffect(() => {
    if (editForm && partnerData) {
      // Update form values with partner data
      setFormikValuesState((prev) => ({ ...prev, ...partnerData }));
    }
  }, [editForm, partnerData]);

  const handleSavePartner = async (
    values: FormValues,
    { resetForm, validateForm }: any
  ) => {
    setLoading(true);

    try {
      const companyId = getCompanyId();
      const partner: Partner = {
        ...values,
        companyId: companyId!,
      };

      const data = await savePartner(partner);
      if (data) {
        if (editForm) {
          toast.success(
            t('SuccessUpdateMessage', {
              name: t('ToastMsg.Partner'),
            })
          );
        } else {
          toast.success(
            t('SuccessMessage', {
              name: t('ToastMsg.Partner'),
            })
          );
        }
      }
      if (data && editForm) {
        refetchPartner();
      }
      navigate(-1);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
      resetForm();
    }
  };

  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={
          editForm ? t('PartnerForm.editPartner') : t('PartnerForm.addPartner')
        }
      />
      {loading ? (
        <Spinner />
      ) : (
        <Formik
          initialValues={formikValuesState}
          validationSchema={validationSchema}
          onSubmit={handleSavePartner}
          enableReinitialize={editForm}
        >
          {({ values, errors, handleChange, handleSubmit, resetForm }) => (
            <form onSubmit={handleSubmit}>
              <div className="group relative z-0 mb-6 w-full">
                <DefaultInput
                  type="text"
                  name="name"
                  id="name"
                  placeholder=" "
                  onChange={handleChange}
                  errors={errors}
                  value={values.name}
                  label={t('PartnerForm.nameLabel')}
                />
              </div>

              {/* <button
                type="submit"
                className="focus:shadow-outline float-right mb-4 rounded-sm bg-blue-700 px-3 py-1 text-white hover:bg-blue-500 focus:outline-none"
                disabled={loading}
              >
                {t('PartnerForm.submitButton')}
              </button>
              <button
                type="button"
                id="cancelForm"
                className="focus:shadow-outline ml-3 rounded-sm px-3 py-1 hover:bg-blue-500 hover:text-white focus:outline-none"
                onClick={() => {
                  resetForm();
                  navigate(-1);
                }}
              >
                {t('PartnerForm.cancelButton')}
              </button> */}

              <FormButtons resetForm={resetForm} loading={loading} />
            </form>
          )}
        </Formik>
      )}
    </DefaultLayout>
  );
}

export default PartnerForm;
